import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { uploadReceiptPhoto } from '../services/api';
import { resolveUploadAssetUrl } from '../utils/resolveUploadAssetUrl';
import '../styles/resourceManagement.css';

export type StorageLocationResource = 'consumable' | 'reagent';

export interface StorageLocationConfirmPayload {
  location: string;
  /** 代申请人入库时由操作人填写实际签收人 */
  receiver?: string;
  /** 确认收货照片 URL（至少 1 张） */
  receiptPhotoUrls: string[];
}

export interface StorageLocationModalProps {
  show: boolean;
  onHide: () => void;
  resourceType: StorageLocationResource;
  /** 当前条目名称，用于说明文案 */
  itemLabel?: string;
  /** 非申请人代为入库时显示签收人输入 */
  showReceiverInput?: boolean;
  onConfirm: (payload: StorageLocationConfirmPayload) => void | Promise<void>;
}

const titles: Record<StorageLocationResource, string> = {
  consumable: '确认收货并入库',
  reagent: '确认收货并入库'
};

const placeholders: Record<StorageLocationResource, string> = {
  consumable: '例如：耗材柜 A-01、西区货架 3 层',
  reagent: '例如：试剂柜 B-02、低温层 2 号格'
};

const MAX_RECEIPT_PHOTOS = 6;

export const StorageLocationModal: React.FC<StorageLocationModalProps> = ({
  show,
  onHide,
  resourceType,
  itemLabel,
  showReceiverInput = false,
  onConfirm
}) => {
  const [location, setLocation] = useState('');
  const [receiver, setReceiver] = useState('');
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (show) {
      setLocation('');
      setReceiver('');
      setPhotoUrls([]);
      setUploading(false);
      setSubmitting(false);
    }
  }, [show]);

  const handlePickFiles = async (files: FileList | null) => {
    if (!files || files.length === 0 || uploading) return;
    const remain = MAX_RECEIPT_PHOTOS - photoUrls.length;
    if (remain <= 0) return;
    const list = Array.from(files).slice(0, remain);
    setUploading(true);
    try {
      for (const file of list) {
        const res = await uploadReceiptPhoto(file);
        if (!res.success || !res.data?.url) {
          alert(res.error || `上传失败：${file.name}`);
          continue;
        }
        setPhotoUrls((prev) => [...prev, res.data!.url].slice(0, MAX_RECEIPT_PHOTOS));
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removePhoto = (idx: number) => {
    setPhotoUrls((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    const loc = location.trim();
    if (!loc || submitting || uploading) return;
    if (showReceiverInput && !receiver.trim()) return;
    if (photoUrls.length === 0) return;
    setSubmitting(true);
    try {
      await Promise.resolve(
        onConfirm({
          location: loc,
          receiptPhotoUrls: photoUrls,
          ...(showReceiverInput ? { receiver: receiver.trim() } : {})
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const title = titles[resourceType];
  const placeholder = placeholders[resourceType];
  const canSubmit =
    Boolean(location.trim()) &&
    photoUrls.length > 0 &&
    !uploading &&
    (!showReceiverInput || Boolean(receiver.trim()));

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      backdropClassName="lims-storage-modal-backdrop"
      dialogClassName="lims-storage-modal__dialog"
      contentClassName="lims-storage-modal__content"
    >
      <Modal.Header closeButton className="lims-storage-modal__header border-0 pb-0">
        <Modal.Title as="h2" className="lims-storage-modal__title mb-0">
          {title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="lims-storage-modal__body">
        {itemLabel ? (
          <p className="lims-storage-modal__hint mb-3">
            条目：<span className="fw-semibold text-dark">{itemLabel}</span>
            <br />
            请填写放置位置并上传收货照片，提交后将完成入库并更新采购状态。
          </p>
        ) : (
          <p className="lims-storage-modal__hint mb-3">
            请填写放置位置并上传收货照片，提交后将完成入库并更新采购状态。
          </p>
        )}
        <label className="lims-storage-modal__label" htmlFor="lims-storage-location-input">
          实验室存放位置 *
        </label>
        <input
          id="lims-storage-location-input"
          className="lims-storage-modal__input"
          type="text"
          autoComplete="off"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder={placeholder}
          disabled={submitting}
        />

        <label className="lims-storage-modal__label mt-3" htmlFor="lims-receipt-photo-input">
          收货照片 *（最多 {MAX_RECEIPT_PHOTOS} 张）
        </label>
        <input
          id="lims-receipt-photo-input"
          ref={fileInputRef}
          type="file"
          accept="image/*,.heic,.heif"
          multiple
          className="d-none"
          disabled={submitting || uploading || photoUrls.length >= MAX_RECEIPT_PHOTOS}
          onChange={(e) => void handlePickFiles(e.target.files)}
        />
        <div className="d-flex flex-wrap gap-2 align-items-start mb-1">
          {photoUrls.map((url, idx) => {
            const src = resolveUploadAssetUrl(url) || url;
            return (
              <div
                key={`${url}-${idx}`}
                className="position-relative"
                style={{ width: 72, height: 72 }}
              >
                <img
                  src={src}
                  alt={`收货图 ${idx + 1}`}
                  style={{
                    width: 72,
                    height: 72,
                    objectFit: 'cover',
                    borderRadius: 8,
                    border: '1px solid #e5e7eb'
                  }}
                />
                <button
                  type="button"
                  className="btn btn-sm btn-danger position-absolute top-0 end-0 p-0"
                  style={{ width: 20, height: 20, lineHeight: '18px', fontSize: 12, borderRadius: 10 }}
                  onClick={() => removePhoto(idx)}
                  disabled={submitting || uploading}
                  aria-label="删除照片"
                >
                  ×
                </button>
              </div>
            );
          })}
          {photoUrls.length < MAX_RECEIPT_PHOTOS ? (
            <button
              type="button"
              className="lims-storage-modal__input d-inline-flex align-items-center justify-content-center"
              style={{ width: 72, height: 72, padding: 0, cursor: 'pointer' }}
              onClick={() => fileInputRef.current?.click()}
              disabled={submitting || uploading}
            >
              {uploading ? '…' : '+'}
            </button>
          ) : null}
        </div>
        <p className="small text-muted mb-0">请拍摄或上传实物收货照片，便于后续核对。</p>

        {showReceiverInput && (
          <>
            <label className="lims-storage-modal__label mt-3" htmlFor="lims-storage-receiver-input">
              签收人 *
            </label>
            <input
              id="lims-storage-receiver-input"
              className="lims-storage-modal__input"
              type="text"
              autoComplete="name"
              value={receiver}
              onChange={(e) => setReceiver(e.target.value)}
              placeholder="填写实际签收物资的人员姓名"
              disabled={submitting}
            />
            <p className="small text-muted mb-0 mt-2">
              您当前为代申请人入库，请填写实际签收人；若由申请人本人操作则不会出现本项。
            </p>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="lims-storage-modal__footer border-0 d-flex justify-content-end flex-nowrap gap-2">
        <Button
          type="button"
          variant="link"
          className="lims-storage-modal__btn-ghost text-decoration-none"
          onClick={onHide}
          disabled={submitting || uploading}
        >
          取消
        </Button>
        <button
          type="button"
          className="lims-storage-modal__btn-primary"
          onClick={() => void handleSubmit()}
          disabled={submitting || !canSubmit}
        >
          {submitting ? '提交中…' : uploading ? '上传中…' : '确认入库'}
        </button>
      </Modal.Footer>
    </Modal>
  );
};
